// Journey actions (two-axis model, Dean 13 Oct 2026): Hand to Legal · Pause / Resume · Restructure decision. One dialog each, used by both lists and the case file.
import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Scale, PauseCircle, PlayCircle, GitBranch } from "lucide-react";
import { toast } from "sonner";
import { getAgeingHandlers, handCaseToLegal, pauseCaseLegal, restructureDecision } from "@/lib/api";

const err = (e) => e?.response?.data?.detail || e.message;
const plusDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export function HandToLegalButton({ caseRow, journey, size = "sm", className = "", onDone, variant = "amber" }) {
  const [open, setOpen] = useState(false);
  const leg = journey?.legal?.key;
  if (!journey || !["none", "pre_action", "ready_for_handover"].includes(leg) || journey.account?.key === "closed") return null;
  const ready = leg === "ready_for_handover";
  const tone = variant === "ghost" ? "text-amber-900 hover:bg-amber-50"
    : ready ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-600" : "bg-white hover:bg-amber-50 text-amber-900 border-amber-300";
  return (
    <>
      <Button size={size} variant="outline" className={`h-7 px-2 text-[11px] font-semibold ${tone} ${className}`} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title={ready ? "DN and LBA have both expired — hand the file to the legal owner in one step" : "Hand the file to Legal: LoanDisk → Legal, bankruptcy route starts at Eligibility check"}
        data-testid={`hand-to-legal-btn-${caseRow.case_id}`}>
        <Scale className="w-3 h-3 mr-1" /> Hand to Legal
      </Button>
      {open && <HandToLegalDialog caseRow={caseRow} journey={journey} onClose={() => setOpen(false)} onDone={(r) => { setOpen(false); onDone?.(r); }} />}
    </>
  );
}

export function HandToLegalDialog({ caseRow, journey, onClose, onDone }) {
  const [handlers, setHandlers] = useState([]);
  const [owner, setOwner] = useState(caseRow.legal_handler || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { getAgeingHandlers("legal").then((r) => setHandlers(r.handlers || [])).catch(() => setHandlers([])); }, []);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await handCaseToLegal(caseRow.case_id, reason, owner || null);
      toast.success(`${caseRow.customer_name || "File"} handed to Legal${owner ? ` — ${owner}` : " — Unassigned panel"}${r.ld_push?.ok === false ? " · LoanDisk push needs a manual check" : ""}`);
      onDone?.(r);
    } catch (e) { toast.error("Hand to Legal failed: " + err(e)); }
    finally { setBusy(false); }
  };
  const options = [...new Set([owner, caseRow.legal_handler, ...handlers, caseRow.handler].filter(Boolean))];
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="hand-to-legal-dialog" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Scale className="w-4 h-4 text-amber-600" /> Hand to Legal — {caseRow.customer_name}</DialogTitle>
          <DialogDescription className="text-xs">{journey?.line}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <ul className="text-xs text-slate-700 space-y-1 border rounded p-2 bg-slate-50">
            <li>• LoanDisk <span className="font-mono">{caseRow.loan_application_id}</span> → <strong>Legal</strong> (loan_status_id 3) — pushed now</li>
            <li>• Bankruptcy route starts at <strong>Eligibility check</strong>; the G1 checklist, SLAs and route-entry actions open for the legal owner</li>
            <li>• The account stays on the Collections worklist while it is on the arrears report; {caseRow.handler || "the account handler"} keeps the account</li>
          </ul>
          <div>
            <div className="text-xs font-medium mb-1">Legal owner</div>
            <select value={owner} onChange={(e) => setOwner(e.target.value)} className="w-full h-9 border rounded-md px-2 text-sm bg-white" data-testid="hand-to-legal-owner">
              <option value="">— leave for the Unassigned panel —</option>
              {options.map((h) => <option key={h} value={h}>{h}{h === caseRow.handler && !handlers.includes(h) ? " (account handler)" : ""}</option>)}
            </select>
          </div>
          <div>
            <div className="text-xs font-medium mb-1">Reason (optional)</div>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="e.g. DN and LBA expired, no contact since 2 Oct" className="text-sm" data-testid="hand-to-legal-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="hand-to-legal-cancel">Cancel</Button>
          <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={submit} disabled={busy} data-testid="hand-to-legal-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Scale className="w-4 h-4 mr-1.5" />} Hand to Legal
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function PauseLegalDialog({ caseRow, journey, onClose, onDone }) {
  const paused = journey?.legal?.key === "paused";
  const [reason, setReason] = useState("");
  const [reviewAt, setReviewAt] = useState(plusDays(30));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const j = await pauseCaseLegal(caseRow.case_id, { active: !paused, reason, review_at: paused ? null : reviewAt });
      toast.success(paused ? "Legal action resumed" : `Legal action paused — review ${new Date(reviewAt + "T00:00:00").toLocaleDateString("en-GB")}`);
      onDone?.(j);
    } catch (e) { toast.error((paused ? "Resume" : "Pause") + " failed: " + err(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="pause-legal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{paused ? <PlayCircle className="w-4 h-4 text-emerald-700" /> : <PauseCircle className="w-4 h-4 text-slate-700" />} {paused ? "Resume" : "Pause"} legal action — {caseRow.customer_name}</DialogTitle>
          <DialogDescription className="text-xs">{paused ? `Currently: ${journey?.legal?.sub || "paused"}. The bankruptcy route goes back to where it paused.` : "Nothing moves on the legal side while paused; the route is held and the Journey shows Paused with your review date."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            <div className="text-xs font-medium mb-1">Reason{paused ? " (optional)" : ""}</div>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder={paused ? "e.g. plan broken — second missed instalment" : "e.g. payment plan agreed £800/month"} className="text-sm" data-testid="pause-legal-reason" />
          </div>
          {!paused && (
            <div>
              <div className="text-xs font-medium mb-1">Review date</div>
              <Input type="date" value={reviewAt} onChange={(e) => setReviewAt(e.target.value)} className="h-9 w-[180px]" data-testid="pause-legal-review-at" />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="pause-legal-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || (!paused && reason.trim().length < 3)} className={paused ? "bg-emerald-700 hover:bg-emerald-800 text-white" : ""} data-testid="pause-legal-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} {paused ? "Resume legal action" : "Pause legal action"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const DECISIONS = [
  { key: "continue", label: "Yes — legal work continues", hint: "The restructure does not change the legal position; the route carries on from where it is.", tone: "border-rose-300 bg-rose-50 text-rose-900" },
  { key: "pause", label: "Pause while the restructure performs", hint: "Hold the route with a review date; a missed instalment brings it straight back.", tone: "border-slate-300 bg-slate-50 text-slate-900" },
  { key: "close", label: "No — close the legal action", hint: "The restructure stands; the legal action closes as resolved via Collections (reason required).", tone: "border-emerald-300 bg-emerald-50 text-emerald-900" },
];

export function RestructureDecisionDialog({ caseRow, journey, onClose, onDone }) {
  const [decision, setDecision] = useState("continue");
  const [reason, setReason] = useState("");
  const [reviewAt, setReviewAt] = useState(plusDays(30));
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const j = await restructureDecision(caseRow.case_id, { decision, reason, review_at: decision === "pause" ? reviewAt : null });
      toast.success(`Decision recorded — ${DECISIONS.find((d) => d.key === decision).label}`);
      onDone?.(j);
    } catch (e) { toast.error("Decision failed: " + err(e)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-lg" data-testid="restructure-decision-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitBranch className="w-4 h-4 text-orange-700" /> Restructured — is the legal work still pending?</DialogTitle>
          <DialogDescription className="text-xs">{caseRow.customer_name} · {caseRow.loan_application_id} · {journey?.line}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 text-sm">
          {DECISIONS.map((d) => (
            <button key={d.key} type="button" onClick={() => setDecision(d.key)} data-testid={`restructure-decision-${d.key}`}
              className={`w-full text-left rounded-md border px-3 py-2 transition-colors ${decision === d.key ? d.tone + " ring-2 ring-offset-1 ring-slate-300" : "border-slate-200 hover:border-slate-400"}`}>
              <div className="font-medium text-[13px]">{d.label}</div>
              <div className="text-[11px] text-slate-600">{d.hint}</div>
            </button>
          ))}
          {decision === "pause" && (
            <div><div className="text-xs font-medium mb-1">Review date</div><Input type="date" value={reviewAt} onChange={(e) => setReviewAt(e.target.value)} className="h-9 w-[180px]" data-testid="restructure-review-at" /></div>
          )}
          <div>
            <div className="text-xs font-medium mb-1">Reason{decision === "close" ? "" : " (optional)"}</div>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="text-sm" data-testid="restructure-reason" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="restructure-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || (decision === "close" && reason.trim().length < 5)} data-testid="restructure-confirm">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null} Record decision
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
